import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, HStack, Image, Input, Spinner, Stack, Text, useColorModeValue, useToast,
  IconButton, Tooltip, Tag, TagLabel, TagCloseButton,
} from '@chakra-ui/react';
import { FiPaperclip, FiDownload, FiSend } from 'react-icons/fi';
import { useAppearance } from '../theme/AppearanceProvider';
import useChat from '../hooks/useChat';
import useCurrentUser from '../hooks/useCurrentUser';
import useUsers from '../hooks/useUsers';
import { UserDto } from '../models/user';
import { useChatUnread } from '../hooks/useChatRead';
import UserChip from './UserChip';
import { uploadStoredFile, downloadStoredFile, StoredFileScope } from '../hooks/useStoredFiles';

// item 99/193/217 — the append-only chat panel, reused across every thread. Polls every
// few seconds; posting appends (messages are immutable once sent). Supports a file
// attachment per message (stored in-DB via StoredFile, scope=ChatAttachment). `threadKey`
// selects the thread: undefined = company↔accountant (item 99), a project id = project
// chat (item 193), TEAM_THREAD_KEY = the dashboard team channel (item 217).
// item 388 — the API serializes CreatedDate as UTC WITHOUT a zone marker, so the
// browser read it as local and showed "2:05 AM" for a 7:05 PM message. Parse as UTC,
// then render chat-style: time only for today, short date + time otherwise.
const parseUtc = (raw: string): Date => (
  /[zZ]$|[+-]\d{2}:\d{2}$/.test(raw) ? new Date(raw) : new Date(`${raw}Z`)
);
const chatTime = (raw: string): string => {
  const d = parseUtc(raw);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString()
    ? time
    : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
};

// item 391 — day separators (Today / Yesterday / Jul 1)
const dayLabel = (raw: string): string => {
  const d = parseUtc(raw);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// item 391 — inbound bridge messages are stored as "[via Slack — Name] text"; render
// them with a Slack badge + the Slack sender's name instead of the raw bracket
const parseViaSlack = (body: string): { slackName: string | null; text: string } => {
  const m = body.match(/^\[via Slack — ([^\]]+)\]\s*/);
  return m ? { slackName: m[1], text: body.slice(m[0].length) } : { slackName: null, text: body };
};

// item 391 — group consecutive same-author messages within 5 minutes
const sameGroup = (a: { authorUserId: string; createdDate: string }, b: { authorUserId: string; createdDate: string }): boolean => (
  a.authorUserId === b.authorUserId
  && Math.abs(parseUtc(b.createdDate).getTime() - parseUtc(a.createdDate).getTime()) < 5 * 60 * 1000
);

// item 392 — attachments render inline: images preview right in the thread (fetched
// through the authed download endpoint), everything else keeps the download button.
const ChatAttachment: React.FC<{ attachmentId: string }> = ({ attachmentId }) => {
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let revoke: string | null = null;
    let alive = true;
    (async () => {
      try {
        const axios = (await import('axios')).default;
        const apiUrl = process.env.REACT_APP_BASIX_API || '';
        const resp = await axios.get(`${apiUrl}StoredFile/${attachmentId}/download`, { responseType: 'blob' });
        if (alive && String(resp.data?.type ?? '').startsWith('image/')) {
          revoke = URL.createObjectURL(resp.data);
          setImgUrl(revoke);
        }
      } catch { /* falls back to the download button */ }
      if (alive) setChecked(true);
    })();
    return () => {
      alive = false;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [attachmentId]);

  if (imgUrl) {
    return (
      <Image
        src={imgUrl}
        alt="attachment"
        maxH="220px"
        maxW="320px"
        borderRadius="lg"
        mt={2}
        cursor="pointer"
        onClick={() => downloadStoredFile(attachmentId, 'attachment')}
      />
    );
  }
  if (!checked) return null;
  return (
    <Button
      size="xs"
      variant="link"
      leftIcon={<FiDownload />}
      onClick={() => downloadStoredFile(attachmentId, 'attachment')}
    >
      Download attachment
    </Button>
  );
};

// item 395 — real avatars; `railMaxH` sizes the scroll area (the focus page goes
// near-full-height). item 406: the old `title`/`focusable` props were dead since
// item 400 moved the Focus/Undock controls into each host's header — removed.
const ChatPanel: React.FC<{
  threadKey?: string;
  railMaxH?: string;
}> = ({
  threadKey, railMaxH = '340px',
}) => {
  const {
    messages, status, send, isSending,
  } = useChat(threadKey);
  const { user } = useCurrentUser();
  // item 395 — real profile photos on chat avatars (they rendered initials-only)
  const { data: usersData } = useUsers();
  const photoOf = React.useMemo(() => {
    const map: Record<string, string | null | undefined> = {};
    (usersData?.index ?? []).forEach((u: UserDto) => { map[u.id] = u.photoUrl; });
    return map;
  }, [usersData]);
  // item 159 — read-state: highlight unread messages + auto-mark the chat read while it's open
  const { isUnread, markRead, unreadCount } = useChatUnread(threadKey);
  const toast = useToast();
  const [body, setBody] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // item 388 — soft tints (the old brandGreen.900 own-message read as a black bar)
  // item 406 — the green own-message tint is Aurora-only: the Accountant ledger is
  // monochrome by design, so mine/theirs separate by surface tone there instead
  const { aurora } = useAppearance();
  const mineTint = useColorModeValue('brandGreen.50', 'rgba(72, 187, 120, 0.10)');
  const mineBg = aurora ? mineTint : 'ledgerSurfaceAlt';
  const theirBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const railBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');
  // item 406 — the day-separator rule was whiteAlpha in BOTH modes (invisible on light)
  const sepColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');
  const viaSlackColor = useColorModeValue('purple.500', 'purple.300');

  // item 159 — viewing the chat marks it read (the panel is only mounted when shown);
  // setting the watermark recomputes unreadCount to 0, so this settles, no loop.
  useEffect(() => {
    if (unreadCount > 0) markRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);

  const onSend = async () => {
    const text = body.trim();
    if (!text && !file) return;
    setBody('');
    const pending = file;
    setFile(null);
    setBusy(true);
    try {
      let attachmentId: string | undefined;
      if (pending) attachmentId = await uploadStoredFile(pending, StoredFileScope.ChatAttachment);
      await send(text, attachmentId);
    } catch {
      setBody(text);
      setFile(pending);
      toast({ status: 'error', title: 'Message not sent', description: 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  // item 391 — keep the newest message in view (on load + as messages arrive)
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  return (
    <Stack spacing={3}>
      <Box ref={scrollRef} maxH={railMaxH} overflowY="auto" bg={railBg} borderRadius="md" p={3}>
        {status === 'loading' && <Spinner size="sm" />}
        {status === 'success' && messages.length === 0 && (
          <Text fontSize="sm" color="gray.500">No messages yet — start the conversation.</Text>
        )}
        {/* item 208 — denser + full-width: each message is a tight row using the whole
            width (not a narrow content-width box in a left column); mine vs theirs kept
            as a subtle background tint + a right-edge accent on my own messages. */}
        <Stack spacing={0.5}>
          {messages.map((m, i) => {
            const mine = user?.id === m.authorUserId;
            const unread = isUnread(m);
            const prev = i > 0 ? messages[i - 1] : null;
            const newDay = !prev || dayLabel(prev.createdDate) !== dayLabel(m.createdDate);
            const via = parseViaSlack(m.body ?? '');
            // item 394 — two Slack senders can share the fallback author row; never
            // group across DIFFERENT Slack names (Daniel's message merged under Chad's)
            const prevVia = prev ? parseViaSlack(prev.body ?? '') : null;
            const grouped = !newDay && prev != null && sameGroup(prev, m)
              && (prevVia?.slackName ?? null) === (via.slackName ?? null);
            return (
              <React.Fragment key={m.id}>
                {/* item 391 — day separator */}
                {newDay && (
                  <HStack my={2} spacing={3} align="center">
                    <Box flex={1} h="1px" bg={sepColor} />
                    <Text fontSize="2xs" fontWeight="bold" letterSpacing="0.1em" opacity={0.55} textTransform="uppercase">
                      {dayLabel(m.createdDate)}
                    </Text>
                    <Box flex={1} h="1px" bg={sepColor} />
                  </HStack>
                )}
                <Box
                  w="full"
                  bg={mine ? mineBg : theirBg}
                  borderRadius="lg"
                  borderTopRadius={grouped ? 'sm' : 'lg'}
                  px={3}
                  py={grouped ? 1 : 2}
                  mt={grouped ? 0 : 1.5}
                // item 159 — unread (incoming, not yet seen) messages get an accent rail
                  borderLeftWidth={unread ? '3px' : undefined}
                  borderLeftColor={unread ? 'brandGreen.400' : undefined}
                >
                  {/* item 391 — grouped messages skip the repeated header */}
                  {!grouped && (
                  <HStack align="center" spacing={2} minW={0}>
                    <UserChip userId={m.authorUserId} name={via.slackName ?? (m.authorName || 'Unknown')} photoUrl={via.slackName ? undefined : photoOf[m.authorUserId]} size="sm" />
                    {via.slackName && (
                      <Text fontSize="2xs" fontWeight="bold" color={viaSlackColor} letterSpacing="0.06em">via SLACK</Text>
                    )}
                    <Text fontSize="2xs" color="gray.500" flexShrink={0}>{chatTime(m.createdDate)}</Text>
                    {unread && (
                      <Text fontSize="2xs" fontWeight="bold" color="brandGreen.400" letterSpacing="0.08em">NEW</Text>
                    )}
                  </HStack>
                  )}
                  {via.text && <Text fontSize="sm" whiteSpace="pre-wrap" mt={grouped ? 0 : 1} opacity={0.95}>{via.text}</Text>}
                  {m.attachmentId && <ChatAttachment attachmentId={m.attachmentId} />}
                </Box>
              </React.Fragment>
            );
          })}
        </Stack>
      </Box>
      {file && (
        <Tag size="sm" colorScheme="green" w="fit-content">
          <TagLabel>{file.name}</TagLabel>
          <TagCloseButton onClick={() => setFile(null)} />
        </Tag>
      )}
      <HStack>
        <input
          ref={fileRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Tooltip label="Attach a file">
          <IconButton size="sm" variant="ghost" aria-label="Attach" icon={<FiPaperclip />} onClick={() => fileRef.current?.click()} flexShrink={0} />
        </Tooltip>
        <Input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder="Write a message…"
          size="sm"
          borderRadius="full"
          _focus={{ borderColor: 'brandGreen.400', boxShadow: '0 0 0 1px var(--chakra-colors-brandGreen-400)' }}
        />
        <IconButton
          aria-label="Send message"
          icon={<FiSend />}
          size="sm"
          borderRadius="full"
          onClick={onSend}
          isLoading={isSending || busy}
          flexShrink={0}
        />
      </HStack>
    </Stack>
  );
};

ChatPanel.defaultProps = {
  threadKey: undefined,
  railMaxH: '340px',
};

export default ChatPanel;
